import React, { useMemo } from 'react';
import styled from "styled-components";
import moment from "moment";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { NoShowDay } from '@features/reporting/services/ReportingService';
import { BodyLarge, BodySmall } from '@shared/ui/atoms/fonts/Body';
import Colours from '@shared/ui/atoms/Colours';
import { CustomToolTip } from "../components/CustomToolTip";

const AXIS_TICK = { fill: Colours.darkGrey, fontSize: 12, fontFamily: '"soleil", sans-serif' };

// Each day's bookings, split into checked in, no-shows and, for today, not checked in yet
export function NoShowsByDayChart(props: Props) {
  const {days} = props;

  const data = useMemo(() => days.map((day: NoShowDay) => ({
    name: moment(day.date).format('D MMM'),
    checkedIn: day.checkedIn,
    noShows: day.noShows,
    notCheckedInYet: day.notCheckedInYet,
    toolTipTitle: moment(day.date).format('ddd D MMM YYYY'),
  })), [days]);
  const hasNotCheckedInYet = useMemo(() => days.some((day: NoShowDay) => day.notCheckedInYet > 0), [days]);

  return (
    <ChartCard>
      <BodyLarge weight={600} style={{marginBottom: 8}}>Bookings by day</BodyLarge>
      <Legend>
        <LegendItem><Swatch style={{backgroundColor: Colours.blue}} /><BodySmall colour={Colours.darkGrey}>Checked in</BodySmall></LegendItem>
        <LegendItem><Swatch style={{backgroundColor: Colours.orange}} /><BodySmall colour={Colours.darkGrey}>No-shows</BodySmall></LegendItem>
        {hasNotCheckedInYet && <LegendItem><Swatch style={{backgroundColor: Colours.mildGrey}} /><BodySmall colour={Colours.darkGrey}>Not checked in yet</BodySmall></LegendItem>}
      </Legend>
      <div style={{height: 280}}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{top: 8, right: 8, left: -16, bottom: 0}}>
            <CartesianGrid vertical={false} stroke={Colours.lightGrey} />
            <XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} />
            <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip content={<CustomToolTip />} cursor={{fill: Colours.blue10}} />
            {/* The white stroke leaves a gap between the parts of each bar */}
            <Bar dataKey="checkedIn" name="Checked in" stackId="bookings" fill={Colours.blue}
                 stroke={Colours.white} strokeWidth={2} maxBarSize={24} />
            <Bar dataKey="noShows" name="No-shows" stackId="bookings" fill={Colours.orange}
                 stroke={Colours.white} strokeWidth={2} maxBarSize={24} radius={hasNotCheckedInYet ? 0 : [4, 4, 0, 0]} />
            {hasNotCheckedInYet && <Bar dataKey="notCheckedInYet" name="Not checked in yet" stackId="bookings" fill={Colours.mildGrey}
                                        stroke={Colours.white} strokeWidth={2} maxBarSize={24} radius={[4, 4, 0, 0]} />}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
}

interface Props {
  days: NoShowDay[];
}

const ChartCard = styled.div`
  background-color: ${Colours.white};
  border: 1px solid var(--Neutrals-Light-Grey, #EDEDED);
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
`

const Legend = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const Swatch = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 2px;
`
